import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FaArrowDown, FaArrowUp, FaEdit, FaLayerGroup, FaPlus, FaTrash } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import { PERMISSIONS } from '../constants/permissions';
import PermissionGuard from '../components/PermissionGuard';
import BillingBreadcrumbs from '../components/BillingBreadcrumbs';
import {
    apiFetchBillingGroups, apiCreateBillingGroup, apiUpdateBillingGroup, apiDeleteBillingGroup,
    apiFetchGLAccounts, apiCreateGLAccount, apiUpdateGLAccount, apiDeleteGLAccount,
    apiFetchBillingSheets,
} from '../utils/billingSetupApi';
import BillingSheetsSetup from '../components/BillingSheetsSetup';

function GLAccountsSetup() {
    const location = useLocation();
    const { currentTenant } = useTenant();
    const { hasPermission } = usePermissions();
    const tenantId = currentTenant?.id;
    const billingSheetsRef = useRef(null);

    const [activeTab, setActiveTab] = useState(
        location.pathname === '/billing-groups' ? 'billing-groups'
            : location.pathname === '/billing-sheets' ? 'billing-sheets'
            : 'gl-accounts'
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // ── GL Accounts state ────────────────────────────────────────────────
    const [accounts, setAccounts] = useState([]);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [accountForm, setAccountForm] = useState({ code: '', name: '' });
    const [accountErrors, setAccountErrors] = useState({});

    // ── Billing Groups state ─────────────────────────────────────────────
    const [groups, setGroups] = useState([]);
    const [billingSheets, setBillingSheets] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupForm, setGroupForm] = useState({ name: '', description: '' });
    const [groupErrors, setGroupErrors] = useState({});

    // Configuration (groups + GL masters) vs sheet lifecycle permissions
    const canConfig = hasPermission(PERMISSIONS.BILLING_CONFIG_MANAGE);
    const canCreateSheet = hasPermission(PERMISSIONS.BILLING_SHEET_CREATE);
    const canUpdateSheet = hasPermission(PERMISSIONS.BILLING_SHEET_UPDATE);
    const canDeleteSheet = hasPermission(PERMISSIONS.BILLING_SHEET_DELETE);
    const hasBillingGroups = groups.length > 0;

    const accountsSorted = [...accounts].sort((a, b) => {
        if ((a.sort_order ?? 0) !== (b.sort_order ?? 0)) return (a.sort_order ?? 0) - (b.sort_order ?? 0);
        return String(a.code).localeCompare(String(b.code));
    });

    // ── Data fetching ─────────────────────────────────────────────────────

    const reload = async () => {
        if (!tenantId) return;
        setLoading(true);
        try {
            const [fetchedGroups, fetchedAccounts, fetchedSheets] = await Promise.all([
                apiFetchBillingGroups(tenantId),
                apiFetchGLAccounts(tenantId),
                apiFetchBillingSheets(tenantId).catch(() => []),
            ]);
            setGroups(fetchedGroups);
            setAccounts(fetchedAccounts);
            setBillingSheets(fetchedSheets);
        } catch (err) {
            console.error('Failed to load billing setup:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, [tenantId]);

    // ── GL Accounts handlers ─────────────────────────────────────────────

    const openAddAccount = () => {
        setEditingAccount(null);
        setAccountForm({ code: '', name: '' });
        setAccountErrors({});
        setShowAccountModal(true);
    };

    const openEditAccount = (a) => {
        setEditingAccount(a);
        setAccountForm({ code: a.code, name: a.name });
        setAccountErrors({});
        setShowAccountModal(true);
    };

    const handleSaveAccount = async () => {
        const errs = {};
        if (!accountForm.code.trim()) errs.code = 'Code is required';
        if (!accountForm.name.trim()) errs.name = 'Name is required';
        const duplicate = accounts.some(a =>
            a.code.toLowerCase() === accountForm.code.trim().toLowerCase() &&
            (!editingAccount || a.id !== editingAccount.id)
        );
        if (duplicate) errs.code = 'A GL account with this code already exists';
        if (Object.keys(errs).length > 0) { setAccountErrors(errs); return; }

        setSaving(true);
        try {
            const payload = {
                code: accountForm.code.trim().toUpperCase(),
                name: accountForm.name.trim(),
            };
            if (editingAccount) {
                await apiUpdateGLAccount(editingAccount.id, payload);
            } else {
                await apiCreateGLAccount(tenantId, { ...payload, sort_order: accounts.length });
            }
            setShowAccountModal(false);
            await reload();
        } catch (err) {
            alert('Failed to save GL account: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAccount = async (id) => {
        if (!window.confirm('Delete this GL account? Price items with allocations to it will show those amounts as unallocated.')) return;
        setSaving(true);
        try {
            await apiDeleteGLAccount(id, tenantId);
            await reload();
        } catch (err) {
            alert('Failed to delete GL account: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const moveAccount = async (id, dir) => {
        const ordered = accountsSorted;
        const idx = ordered.findIndex(a => a.id === id);
        const newIdx = idx + dir;
        if (idx < 0 || newIdx < 0 || newIdx >= ordered.length) return;

        const a = ordered[idx];
        const b = ordered[newIdx];
        setSaving(true);
        try {
            await Promise.all([
                apiUpdateGLAccount(a.id, { code: a.code, name: a.name, sort_order: b.sort_order }),
                apiUpdateGLAccount(b.id, { code: b.code, name: b.name, sort_order: a.sort_order }),
            ]);
            await reload();
        } catch (err) {
            alert('Failed to reorder: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Billing Groups handlers ──────────────────────────────────────────

    const openAddGroup = () => {
        setEditingGroup(null);
        setGroupForm({ name: '', description: '' });
        setGroupErrors({});
        setShowGroupModal(true);
    };

    const openEditGroup = (g) => {
        setEditingGroup(g);
        setGroupForm({ name: g.name, description: g.description || '' });
        setGroupErrors({});
        setShowGroupModal(true);
    };

    const handleSaveGroup = async () => {
        const errs = {};
        if (!groupForm.name.trim()) errs.name = 'Name is required';
        const duplicate = groups.some(g =>
            g.name.toLowerCase() === groupForm.name.trim().toLowerCase() &&
            (!editingGroup || g.id !== editingGroup.id)
        );
        if (duplicate) errs.name = 'A billing group with this name already exists';
        if (Object.keys(errs).length > 0) { setGroupErrors(errs); return; }

        setSaving(true);
        try {
            const payload = {
                name: groupForm.name.trim(),
                description: groupForm.description.trim(),
            };
            if (editingGroup) {
                await apiUpdateBillingGroup(editingGroup.id, payload);
            } else {
                await apiCreateBillingGroup(tenantId, { ...payload, sort_order: groups.length });
            }
            setShowGroupModal(false);
            await reload();
        } catch (err) {
            alert('Failed to save billing group: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteGroup = async (id) => {
        if (!window.confirm('Delete this billing group? GL Accounts assigned to it will become unassigned.')) return;
        setSaving(true);
        try {
            await apiDeleteBillingGroup(id, tenantId);
            await reload();
        } catch (err) {
            alert('Failed to delete billing group: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const moveGroup = async (id, dir) => {
        const idx = groups.findIndex(g => g.id === id);
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= groups.length) return;
        const a = groups[idx];
        const b = groups[newIdx];
        setSaving(true);
        try {
            await Promise.all([
                apiUpdateBillingGroup(a.id, {
                    name: a.name,
                    description: a.description,
                    sort_order: b.sort_order,
                }),
                apiUpdateBillingGroup(b.id, {
                    name: b.name,
                    description: b.description,
                    sort_order: a.sort_order,
                }),
            ]);
            await reload();
        } catch (err) {
            alert('Failed to reorder: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // ── render ────────────────────────────────────────────────────────────

    if (loading && groups.length === 0 && accounts.length === 0) {
        return (
            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--theme-text-muted)' }}>
                <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />
                Loading billing setup…
            </div>
        );
    }

    return (
        <div style={{ padding: '28px 32px', minHeight: '100%', backgroundColor: 'var(--brand-cream, #fafaf7)' }}>

            <BillingBreadcrumbs items={[
                { label: 'Billing', to: '/billing' },
                { label: 'Setup', to: '/billing?tab=sheets' },
                {
                    label: activeTab === 'billing-sheets' ? 'Sheet configs'
                        : activeTab === 'billing-groups' ? 'Billing groups'
                        : 'GL accounts',
                },
            ]} />

            {/* Page header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h2 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>Billing Setup</h2>
                    <p style={{ color: 'var(--theme-text-secondary)', marginTop: '6px', marginBottom: 0, fontSize: '0.95rem' }}>
                        Manage billing groups and GL account codes for your billing sheet structure.
                    </p>
                </div>
                <PermissionGuard
                    permission={activeTab === 'billing-sheets' ? PERMISSIONS.BILLING_SHEET_CREATE : PERMISSIONS.BILLING_CONFIG_MANAGE}
                    showDisabled
                    tooltip="You need billing setup permissions to manage this"
                >
                    {activeTab === 'gl-accounts' ? (
                        <Button
                            variant="primary"
                            onClick={() => openAddAccount()}
                            className="d-flex align-items-center gap-2"
                            style={{ fontWeight: 600 }}
                            disabled={!canConfig || saving}
                        >
                            <FaPlus style={{ fontSize: '0.8rem' }} />Add GL Account
                        </Button>
                    ) : activeTab === 'billing-groups' ? (
                        <Button
                            variant="primary"
                            onClick={openAddGroup}
                            className="d-flex align-items-center gap-2"
                            style={{ fontWeight: 600 }}
                            disabled={!canConfig || saving}
                        >
                            <FaPlus style={{ fontSize: '0.8rem' }} />Add Billing Group
                        </Button>
                    ) : (
                        <Button
                            variant="dark"
                            onClick={() => billingSheetsRef.current?.openAdd()}
                            className="d-flex align-items-center gap-2"
                            style={{ fontWeight: 600 }}
                            disabled={!canCreateSheet || !hasBillingGroups || saving}
                        >
                            <FaPlus style={{ fontSize: '0.8rem' }} />Add Billing Sheet
                        </Button>
                    )}
                </PermissionGuard>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid var(--theme-border)', marginBottom: '24px' }}>
                {[
                    { key: 'billing-sheets', label: 'Billing Sheet', count: billingSheets.length },
                    { key: 'billing-groups', label: 'Billing Group', count: groups.length },
                    { key: 'gl-accounts', label: 'GL Account', count: accounts.length },
                ].map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === tab.key ? '2px solid var(--theme-text)' : '2px solid transparent',
                            marginBottom: '-2px',
                            padding: '10px 20px',
                            fontWeight: activeTab === tab.key ? 700 : 500,
                            color: activeTab === tab.key ? 'var(--theme-text)' : 'var(--theme-text-secondary)',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                            outline: 'none',
                        }}
                    >
                        {tab.label}
                        <span style={{
                            marginLeft: '8px',
                            fontSize: '0.72rem',
                            backgroundColor: activeTab === tab.key ? 'var(--theme-text)' : 'var(--theme-surface-muted)',
                            color: activeTab === tab.key ? 'var(--theme-page-bg)' : 'var(--theme-text-secondary)',
                            borderRadius: '10px',
                            padding: '1px 8px',
                            fontWeight: 600,
                        }}>
                            {tab.count}
                        </span>
                    </button>
                ))}
            </div>

            {/* ── Billing Groups tab ─────────────────────────────────────────── */}
            {activeTab === 'billing-groups' && (
                <>
                    <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '24px', fontSize: '0.9rem' }}>
                        Define the top-level groupings for GL accounts (e.g. Standard Charges, Additional Charges).
                        Combine groups and care types into named billing sheets on the Billing Sheets tab.
                    </p>

                    {groups.length === 0 ? (
                        <div style={{
                            border: '2px dashed var(--theme-border)', borderRadius: '10px', padding: '60px 40px',
                            textAlign: 'center', color: 'var(--theme-text-muted)',
                        }}>
                            <FaLayerGroup style={{ fontSize: '2.5rem', marginBottom: '16px', opacity: 0.4 }} />
                            <p style={{ fontSize: '1rem', marginBottom: '8px', fontWeight: 600 }}>No billing groups yet</p>
                            <p style={{ fontSize: '0.875rem', marginBottom: '20px' }}>
                                Create billing groups like "Standard Charges" and "Additional Charges" to structure your billing sheet.
                            </p>
                            {canConfig && (
                                <Button variant="primary" onClick={openAddGroup}>
                                    <FaPlus style={{ marginRight: '6px' }} />Create First Billing Group
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            backgroundColor: 'var(--theme-surface)', border: '1px solid var(--theme-border)', borderRadius: '8px',
                            overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                            <Table bordered={false} size="sm" style={{ margin: 0 }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--theme-table-header-bg)', borderBottom: '1px solid var(--theme-table-border-strong)' }}>
                                        <th style={TH}>#</th>
                                        <th style={TH}>Name</th>
                                        <th style={TH}>Description</th>
                                        <th style={{ ...TH, width: '120px', textAlign: 'center' }}>Order</th>
                                        {canConfig && <th style={{ ...TH, width: '120px', textAlign: 'center' }}>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groups.map((g, idx) => (
                                        <tr key={g.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={TD}>{idx + 1}</td>
                                            <td style={{ ...TD, fontWeight: 600, color: 'var(--theme-table-text)' }}>{g.name}</td>
                                            <td style={{ ...TD, color: 'var(--theme-table-text-secondary)', fontSize: '0.875rem' }}>{g.description || '—'}</td>
                                            <td style={{ ...TD, textAlign: 'center' }}>
                                                <div className="d-flex justify-content-center gap-1">
                                                    <Button variant="outline-secondary" size="sm"
                                                        disabled={idx === 0 || !canConfig || saving}
                                                        onClick={() => moveGroup(g.id, -1)}
                                                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                                                        <FaArrowUp />
                                                    </Button>
                                                    <Button variant="outline-secondary" size="sm"
                                                        disabled={idx === groups.length - 1 || !canConfig || saving}
                                                        onClick={() => moveGroup(g.id, 1)}
                                                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                                                        <FaArrowDown />
                                                    </Button>
                                                </div>
                                            </td>
                                            {canConfig && (
                                                <td style={{ ...TD, textAlign: 'center' }}>
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Button variant="link" size="sm" onClick={() => openEditGroup(g)}
                                                            style={{ padding: '2px 8px', color: '#3b82f6' }}>
                                                            <FaEdit />
                                                        </Button>
                                                        <Button variant="link" size="sm" onClick={() => handleDeleteGroup(g.id)}
                                                            disabled={saving}
                                                            style={{ padding: '2px 8px', color: '#ef4444' }}>
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}

            {/* ── Billing Sheets tab ─────────────────────────────────────────── */}
            {activeTab === 'billing-sheets' && (
                <BillingSheetsSetup
                    ref={billingSheetsRef}
                    tenantId={tenantId}
                    groups={groups}
                    glAccounts={accountsSorted}
                    canCreate={canCreateSheet}
                    canUpdate={canUpdateSheet}
                    canDelete={canDeleteSheet}
                    canConfig={canConfig}
                    saving={saving}
                    setSaving={setSaving}
                    onReload={reload}
                />
            )}

            {/* ── GL Accounts tab ────────────────────────────────────────────── */}
            {activeTab === 'gl-accounts' && (
                <>
                    <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '24px', fontSize: '0.9rem' }}>
                        Define GL account codes (e.g. LAUNDRY001, MEDI001). Price items allocate portions of
                        their price to these accounts. Assign accounts to billing groups per sheet on the Billing Sheet tab.
                    </p>

                    {accountsSorted.length === 0 ? (
                        <div style={{
                            border: '2px dashed var(--theme-border)', borderRadius: '10px', padding: '60px 40px',
                            textAlign: 'center', color: 'var(--theme-text-muted)',
                        }}>
                            <FaLayerGroup style={{ fontSize: '2.5rem', marginBottom: '16px', opacity: 0.4 }} />
                            <p style={{ fontSize: '1rem', marginBottom: '8px', fontWeight: 600 }}>No GL accounts yet</p>
                            <p style={{ fontSize: '0.875rem', marginBottom: '20px' }}>
                                Create GL account codes to allocate price item revenue for billing.
                            </p>
                            {canConfig && (
                                <Button variant="primary" onClick={() => openAddAccount()}>
                                    <FaPlus style={{ marginRight: '6px' }} />Create First GL Account
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            backgroundColor: 'var(--theme-surface)', border: '1px solid var(--theme-border)', borderRadius: '8px',
                            overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                            <Table bordered={false} size="sm" style={{ margin: 0 }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--theme-table-header-bg)', borderBottom: '1px solid var(--theme-table-border-strong)' }}>
                                        <th style={{ ...TH, width: '40px' }}>#</th>
                                        <th style={{ ...TH, width: '200px' }}>Code</th>
                                        <th style={TH}>Name</th>
                                        <th style={{ ...TH, width: '120px', textAlign: 'center' }}>Order</th>
                                        {canConfig && <th style={{ ...TH, width: '100px', textAlign: 'center' }}>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {accountsSorted.map((a, idx) => (
                                        <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ ...TD, color: 'var(--theme-table-text-secondary)', fontSize: '0.8rem' }}>{idx + 1}</td>
                                            <td style={TD}>
                                                <code style={{
                                                    backgroundColor: 'var(--theme-table-header-bg)', padding: '3px 8px',
                                                    borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--theme-table-header-text)',
                                                }}>
                                                    {a.code}
                                                </code>
                                            </td>
                                            <td style={{ ...TD, fontSize: '0.9rem', color: 'var(--theme-table-text)' }}>{a.name}</td>
                                            <td style={{ ...TD, textAlign: 'center' }}>
                                                <div className="d-flex justify-content-center gap-1">
                                                    <Button variant="outline-secondary" size="sm"
                                                        disabled={idx === 0 || !canConfig || saving}
                                                        onClick={() => moveAccount(a.id, -1)}
                                                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                                                        <FaArrowUp />
                                                    </Button>
                                                    <Button variant="outline-secondary" size="sm"
                                                        disabled={idx === accountsSorted.length - 1 || !canConfig || saving}
                                                        onClick={() => moveAccount(a.id, 1)}
                                                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                                                        <FaArrowDown />
                                                    </Button>
                                                </div>
                                            </td>
                                            {canConfig && (
                                                <td style={{ ...TD, textAlign: 'center' }}>
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Button variant="link" size="sm" onClick={() => openEditAccount(a)}
                                                            style={{ padding: '2px 8px', color: '#3b82f6' }}>
                                                            <FaEdit />
                                                        </Button>
                                                        <Button variant="link" size="sm" onClick={() => handleDeleteAccount(a.id)}
                                                            disabled={saving}
                                                            style={{ padding: '2px 8px', color: '#ef4444' }}>
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}

            {/* ── GL Account modal ──────────────────────────────────────────────── */}
            <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingAccount ? 'Edit GL Account' : 'Add GL Account'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Code <span style={{ color: 'red' }}>*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. LAUNDRY001"
                                value={accountForm.code}
                                onChange={e => {
                                    setAccountForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }));
                                    if (accountErrors.code) setAccountErrors(prev => ({ ...prev, code: null }));
                                }}
                                isInvalid={!!accountErrors.code}
                            />
                            <Form.Control.Feedback type="invalid">{accountErrors.code}</Form.Control.Feedback>
                            <Form.Text className="text-muted">The GL code used in Sage EVO exports</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Display Name <span style={{ color: 'red' }}>*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Laundry"
                                value={accountForm.name}
                                onChange={e => {
                                    setAccountForm(prev => ({ ...prev, name: e.target.value }));
                                    if (accountErrors.name) setAccountErrors(prev => ({ ...prev, name: null }));
                                }}
                                isInvalid={!!accountErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">{accountErrors.name}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Text className="text-muted">
                            Assign this account to billing groups per sheet on the Billing Sheet tab.
                        </Form.Text>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAccountModal(false)} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={handleSaveAccount} disabled={saving} style={{ fontWeight: 600 }}>
                        {saving ? <><Spinner size="sm" animation="border" style={{ marginRight: '6px' }} />Saving…</> : (editingAccount ? 'Save Changes' : 'Add GL Account')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Billing Group modal ───────────────────────────────────────────── */}
            <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingGroup ? 'Edit Billing Group' : 'Add Billing Group'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name <span style={{ color: 'red' }}>*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Standard Charges"
                                value={groupForm.name}
                                onChange={e => {
                                    setGroupForm(prev => ({ ...prev, name: e.target.value }));
                                    if (groupErrors.name) setGroupErrors(prev => ({ ...prev, name: null }));
                                }}
                                isInvalid={!!groupErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">{groupErrors.name}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Optional description"
                                value={groupForm.description}
                                onChange={e => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGroupModal(false)} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={handleSaveGroup} disabled={saving} style={{ fontWeight: 600 }}>
                        {saving ? <><Spinner size="sm" animation="border" style={{ marginRight: '6px' }} />Saving…</> : (editingGroup ? 'Save Changes' : 'Add Billing Group')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

const TH = {
    padding: '11px 14px',
    color: 'var(--theme-table-header-text)',
    fontSize: '0.76rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    backgroundColor: 'var(--theme-table-header-bg)',
};

const TD = {
    padding: '10px 16px',
    verticalAlign: 'middle',
};

export default GLAccountsSetup;
